import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, Spinner, TextInput } from 'flowbite-react';
import * as wasm from 'sched';
import { WorkspaceContext } from '../../context/WorkspaceContext';
import { FiUsers } from 'react-icons/fi';

interface AddPersonToTeamFormProps {
  personId: string;
  onSubmit: (command: wasm.DatabaseCommand) => Promise<void> | void;
  onAdded?: () => void;
}

interface TeamSearchResult {
  id: string;
  name: string;
}

const AddPersonToTeamForm: React.FC<AddPersonToTeamFormProps> = ({ personId, onSubmit, onAdded }) => {
  const { safeData } = useContext(WorkspaceContext);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<TeamSearchResult | null>(null);
  const [results, setResults] = useState<TeamSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
          inputRef.current && !inputRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    const run = async () => {
      if (!safeData || !query) {
        setResults([]);
        setIsSearching(false);
        return;
      }
      setIsSearching(true);
      try {
        const resp = await wasm.search_teams(safeData, query, 6);
        const mapped: TeamSearchResult[] = resp.map((t) => {
          const team = t.team();
          return { id: team.id(), name: team.name() };
        });
        setResults(mapped);
      } catch (err) {
        console.error('search_teams failed', err);
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    };
    const t = setTimeout(run, 250);
    return () => clearTimeout(t);
  }, [query, safeData]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    setSubmitting(true);
    try {
      const command: wasm.DatabaseCommand = {
        type: 'SetTeamMembership',
        membership: {
          member: { Person: personId },
          team: selected.id,
        },
        enabled: true,
      };
      await onSubmit(command);
      setSelected(null);
      setQuery('');
      setShowDropdown(false);
      if (onAdded) onAdded();
    } catch (err) {
      console.error('Error adding person to team', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex items-center gap-2">
      <div className="flex-1 relative">
        <div className="flex items-center gap-2">
          <FiUsers className="text-cm-text-muted" />
          <TextInput
            ref={inputRef}
            value={selected ? selected.name : query}
            onChange={(e) => {
              setSelected(null);
              setQuery(e.target.value);
              setShowDropdown(true);
            }}
            placeholder="Search teams to add..."
            onFocus={() => setShowDropdown(true)}
            className="w-full"
          />
        </div>
        {showDropdown && (query.length > 0 || isSearching) && (
          <div
            ref={dropdownRef}
            className="absolute z-50 mt-1 w-full bg-cm-bg-surface border border-cm-border-subtle rounded-md shadow-lg max-h-60 overflow-auto"
          >
            {isSearching ? (
              <div className="px-3 py-2 text-sm text-cm-text-secondary flex items-center gap-2">
                <Spinner size="sm" /> Searching...
              </div>
            ) : results.length > 0 ? (
              results.map((t) => (
                <button
                  type="button"
                  key={t.id}
                  className="block w-full text-left px-3 py-2 hover:bg-cm-bg-hover"
                  onClick={() => {
                    setSelected(t);
                    setQuery('');
                    setShowDropdown(false);
                  }}
                >
                  <span className="text-sm">{t.name}</span>
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-cm-text-muted">No matches</div>
            )}
          </div>
        )}
      </div>
      <Button type="submit" size="sm" disabled={!selected || submitting}>
        {submitting ? 'Adding…' : 'Add'}
      </Button>
    </form>
  );
};

export default AddPersonToTeamForm;
